import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, FlaskConical, Star, Check, Truck, ShieldCheck, RotateCcw, Lock } from "lucide-react";
import { fetchProducts, fetchTestimonials } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";
import { ReviewCard } from "./Reviews";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/ca7d7df0-fe7c-4795-8e54-8086fc594de7/images/508b3ab790cbf6ab9bface0d6d75a63cf9445e75b3c93b0337bd17b29ddc9e2d.png";

const LIFESTYLE =
  "/lifestyle/lux-nightlife.png";

const LIFESTYLE_GALLERY = [
  {
    src: "/lifestyle/hold-longevid-1.png",
    label: "THE RITUAL",
    caption: "Two tablets every morning. No excuses.",
  },
  {
    src: "/lifestyle/hold-tenacit-1.png",
    label: "THE STANDARD",
    caption: "Three capsules before bed. Wake up sharper.",
  },
  {
    src: "/lifestyle/hold-standard.png",
    label: "THE FORMULA",
    caption: "Sharp, deliberate, unmistakable.",
  },
  {
    src: "/lifestyle/hold-edge.png",
    label: "THE EDGE",
    caption: "You don't enter rooms. You take them.",
  },
  {
    src: "/lifestyle/hold-longevid-2.png",
    label: "TRAINING DAYS",
    caption: "Stamina, drive, recovery — compounded.",
  },
  {
    src: "/lifestyle/hold-tenacit-2.png",
    label: "EVERY MORNING",
    caption: "Built for the man who's already winning.",
  },
];

const ATTRACTION_GALLERY = [
  {
    src: "/lifestyle/sex-appeal-1.png",
    label: "PRESENCE",
    caption: "She notices the man who's sure of himself.",
  },
  {
    src: "/lifestyle/sex-appeal-2.png",
    label: "CHEMISTRY",
    caption: "Drive, confidence, and the room knows it.",
  },
  {
    src: "/lifestyle/sex-appeal-3.png",
    label: "MAGNETISM",
    caption: "What you bring home is what you bring to her.",
  },
  {
    src: "/lifestyle/sex-appeal-4.png",
    label: "FOCUS",
    caption: "She's drawn to the man who's already locked in.",
  },
  {
    src: "/lifestyle/sex-appeal-5.png",
    label: "DOMINION",
    caption: "Quiet confidence rewires the room.",
  },
  {
    src: "/lifestyle/sex-appeal-6.png",
    label: "COMMAND",
    caption: "Presence is the most expensive thing you can wear.",
  },
];

/** Inline US flag — kept simple, brand-aware (no copyrighted assets). */
const UsFlagIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 20 14"
    className={className}
    aria-hidden="true"
    preserveAspectRatio="xMidYMid meet"
  >
    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
      <rect
        key={i}
        x="0"
        y={i * 2}
        width="20"
        height="2"
        fill={i % 2 === 0 ? "#D10000" : "#F5F5F5"}
      />
    ))}
    <rect x="0" y="0" width="9" height="8" fill="#0B2545" />
    {[...Array(5)].map((_, r) =>
      [...Array(4)].map((_, c) => (
        <circle
          key={`${r}-${c}`}
          cx={1 + c * 2}
          cy={1 + r * 1.5}
          r="0.45"
          fill="#F5F5F5"
        />
      ))
    )}
  </svg>
);

const Home = () => {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState({ items: [], stats: null });
  const { addItem } = useCart();

  useEffect(() => {
    fetchProducts().then((d) => setProducts(d.products || [])).catch(() => {});
    fetchTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  return (
    <div className="bg-[#050505] text-white">
      {/* HERO */}
      <section
        className="relative min-h-[92vh] grain overflow-hidden"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.7) 50%, #050505 100%), url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[92vh]">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 mb-8"
            >
              <span className="h-1.5 w-1.5 bg-[#D10000] rounded-full" />
              <span className="font-display text-xs tracking-[0.3em] text-white/70">
                BE THE NATURAL SELECTION
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-display uppercase leading-[0.85] tracking-tight text-balance"
              style={{ fontSize: "clamp(2.75rem, 7.5vw, 7rem)" }}
            >
              <span className="block">Built for men</span>
              <span className="block text-stroke">who refuse to be</span>
              <span className="block text-[#D10000]">average.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-8 max-w-xl text-white/75 text-lg leading-relaxed font-body"
            >
              Advanced male performance support for energy, confidence,
              stamina, and presence. Naturally formulated. Clinically dosed.
              Built for the man who's already winning and wants more.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl"
            >
              {[
                "Boost drive & libido",
                "Enhance performance",
                "Support testosterone",
                "Improve recovery",
                "Feel dominant & focused",
                "Naturally formulated",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/85">
                  <Check className="h-4 w-4 text-[#D10000] shrink-0 mt-1" />
                  <span className="font-body">{b}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="inline-flex items-center gap-3 bg-[#D10000] hover:bg-[#A30000] transition-colors text-white font-display text-xl tracking-widest uppercase px-8 py-4"
              >
                Shop the Stack
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/reviews"
                data-testid="hero-reviews-cta"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-white text-white font-display text-xl tracking-widest uppercase px-8 py-4 transition-colors"
              >
                Read Reviews
              </Link>
            </motion.div>

            <div className="mt-12 flex items-center gap-6 text-white/40 text-xs font-body uppercase tracking-widest">
              <span className="flex items-center gap-2">
                <Leaf className="h-4 w-4 text-[#D10000]" /> Naturally
                Formulated
              </span>
              <span className="hidden sm:flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-[#D10000]" /> Lab Tested
              </span>
              <span className="hidden md:flex items-center gap-2">
                <UsFlagIcon className="h-4 w-5" /> Made in USA
              </span>
            </div>
          </div>

          {/* Floating products — mobile-friendly */}
          <div className="lg:col-span-5 relative min-h-[400px] sm:min-h-[500px] lg:min-h-[640px] mt-8 lg:mt-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: -8 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="absolute top-0 left-0 sm:-top-6 lg:-top-24 lg:-left-8 w-[55vw] sm:w-[16rem] md:w-[20rem] lg:w-[26rem] xl:w-[30rem]"
            >
              <div className="animate-float">
                <img
                  src="/products/longevi-d-hero.png"
                  alt="Longevi-D"
                  className="w-full h-auto drop-shadow-[0_40px_80px_rgba(209,0,0,0.45)]"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: 10 }}
              animate={{ opacity: 1, scale: 1, rotate: 10 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="absolute top-16 right-0 sm:top-20 lg:-right-8 w-[60vw] sm:w-[18rem] md:w-[22rem] lg:w-[28rem] xl:w-[34rem]"
            >
              <div className="animate-float" style={{ animationDelay: "1.5s" }}>
                <img
                  src="/products/tenaci-t-hero.png"
                  alt="Tenaci-T"
                  className="w-full h-auto drop-shadow-[0_40px_80px_rgba(209,0,0,0.5)]"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom ticker */}
        <div className="relative z-10 border-y border-white/10 overflow-hidden">
          <div className="flex whitespace-nowrap animate-marquee">
            {[...Array(2)].map((_, k) => (
              <div key={k} className="flex shrink-0">
                {[
                  "DRIVE",
                  "STAMINA",
                  "TESTOSTERONE",
                  "CONFIDENCE",
                  "EDGE",
                  "RECOVERY",
                  "POWER",
                  "DOMINANCE",
                ].map((w, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="font-display text-2xl md:text-4xl tracking-widest uppercase text-white/10 px-8 py-4 flex items-center gap-8"
                  >
                    {w}
                    <span className="h-1.5 w-1.5 bg-[#D10000] rounded-full" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="featured-products-section"
      >
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-12 md:mb-16 border-b border-white/10 pb-8">
            <div>
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                01 / THE ARSENAL
              </div>
              <h2
                className="font-display uppercase leading-[0.9] tracking-tight"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
              >
                Engineered <br className="hidden md:block" /> for Men.
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-products-link"
              className="hidden md:inline-flex items-center gap-2 text-white/60 hover:text-white font-display tracking-widest text-sm"
            >
              ALL PRODUCTS <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map((p, idx) => (
            <Reveal key={p.id} delay={idx * 0.1}>
              <article
                className="group relative bg-[#0A0A0A] border border-white/10 hover:border-[#D10000]/50 transition-all duration-500 hover:-translate-y-1 overflow-hidden"
                data-testid={`product-card-${p.id}`}
              >
                <div className="aspect-[4/5] relative overflow-hidden bg-[#0A0A0A]">
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(209,0,0,0.18), transparent 60%)",
                    }}
                  />
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 m-auto w-2/3 h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 font-display text-xs tracking-[0.3em] text-white/60">
                    {p.count}
                  </div>
                  <div className="absolute top-4 right-4 font-display text-xs tracking-[0.3em] text-[#D10000]">
                    {p.tagline.split(" ").slice(0, 2).join(" ").toUpperCase()}
                  </div>
                </div>
                <div className="p-6 md:p-8 border-t border-white/10 flex items-center justify-between gap-6">
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl tracking-tight uppercase leading-none">
                      {p.name}
                    </h3>
                    <p className="text-white/50 text-sm mt-2 font-body">
                      {p.subtitle}
                    </p>
                    <div className="font-display text-2xl mt-4 tracking-wider">
                      ${p.price.toFixed(2)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => addItem(p, 1)}
                      data-testid={`add-${p.id}-to-cart-button`}
                      className="bg-[#D10000] hover:bg-[#A30000] transition-colors text-white font-display text-sm tracking-widest uppercase px-5 py-3"
                    >
                      Add to Cart
                    </button>
                    <Link
                      to={`/product/${p.id}`}
                      data-testid={`view-${p.id}-details-link`}
                      className="text-center border border-white/15 hover:border-white text-white font-display text-sm tracking-widest uppercase px-5 py-3"
                    >
                      Details
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUST STRIP — friction removal */}
      <section
        className="border-y border-white/10 bg-[#070707]"
        data-testid="trust-strip"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-5 gap-6">
          {[
            { Icon: Truck, t: "Free US Shipping", s: "Orders $75+" },
            { Icon: RotateCcw, t: "30-Day Guarantee", s: "Money back, no friction" },
            { Icon: Lock, t: "Secure Checkout", s: "Stripe-protected" },
            { Icon: FlaskConical, t: "Lab Tested", s: "Third-party verified" },
            { Icon: ShieldCheck, t: "Made in USA", s: "cGMP-certified" },
          ].map(({ Icon, t, s }, i) => (
            <div key={i} className="flex items-start gap-3">
              <Icon className="h-5 w-5 text-[#D10000] mt-1 shrink-0" />
              <div>
                <div className="font-display tracking-widest uppercase text-sm">
                  {t}
                </div>
                <div className="text-white/45 text-xs mt-0.5">{s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* THE CODE / Why */}
      <section className="relative py-24 md:py-32 grain border-y border-white/5">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 80% 20%, rgba(209,0,0,0.25), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                02 / THE CODE
              </div>
              <h2
                className="font-display uppercase leading-[0.9]"
                style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
              >
                No shortcuts. <br />
                <span className="text-stroke">Just signal.</span>
              </h2>
              <p className="mt-8 text-white/70 leading-relaxed max-w-md">
                Every Primenax formula is built on naturally-sourced
                ingredients, clinically-recognized doses, and zero filler. We
                don't sell hype. We sell what works.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {[
              {
                icon: Leaf,
                title: "Naturally Formulated",
                copy: "Botanicals, adaptogens, and minerals chosen for evidence — not buzz.",
              },
              {
                icon: FlaskConical,
                title: "Third-Party Tested",
                copy: "Every batch verified for identity, potency, and purity.",
              },
              {
                icon: UsFlagIcon,
                title: "Made in USA",
                copy: "Manufactured in cGMP-certified facilities. No compromises.",
              },
              {
                icon: Star,
                title: "Designed for Men",
                copy: "Doses, ratios, and stacks built for male physiology.",
              },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-[#0A0A0A] p-8 md:p-10 h-full">
                  <f.icon className="h-7 w-9 text-[#D10000] mb-6" />
                  <h3 className="font-display text-2xl tracking-tight uppercase mb-3">
                    {f.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed text-sm">
                    {f.copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — rich reviews with photos */}
      <section
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="testimonials-section"
      >
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
            <div className="lg:col-span-7">
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                03 / THE PROOF
              </div>
              <h2
                className="font-display uppercase leading-[0.9] tracking-tight"
                style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
              >
                Real men. <br className="md:hidden" />
                <span className="text-stroke">Real results.</span>
              </h2>
            </div>
            {testimonials.stats && (
              <div className="lg:col-span-5 grid grid-cols-3 gap-px bg-white/5">
                <div className="bg-[#0A0A0A] p-5 text-center">
                  <div className="font-display text-2xl md:text-3xl tracking-wider leading-none">
                    {testimonials.stats.rating_avg.toFixed(1)}
                  </div>
                  <div className="font-display text-[10px] tracking-[0.25em] text-white/40 mt-2 uppercase">
                    Avg rating
                  </div>
                </div>
                <div className="bg-[#0A0A0A] p-5 text-center">
                  <div className="font-display text-2xl md:text-3xl tracking-wider leading-none">
                    {testimonials.stats.rating_count.toLocaleString()}+
                  </div>
                  <div className="font-display text-[10px] tracking-[0.25em] text-white/40 mt-2 uppercase">
                    Reviews
                  </div>
                </div>
                <div className="bg-[#0A0A0A] p-5 text-center">
                  <div className="font-display text-2xl md:text-3xl tracking-wider leading-none">
                    {testimonials.stats.reorder_pct}%
                  </div>
                  <div className="font-display text-[10px] tracking-[0.25em] text-white/40 mt-2 uppercase">
                    Reorder
                  </div>
                </div>
              </div>
            )}
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(testimonials.items || []).slice(0, 6).map((t, i) => (
            <Reveal key={t.id || i} delay={i * 0.05}>
              <ReviewCard r={t} />
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.4}>
          <div className="mt-12 flex justify-center">
            <Link
              to="/reviews"
              data-testid="home-all-reviews-link"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-[#D10000] text-white font-display text-base tracking-widest uppercase px-8 py-4 transition-colors"
            >
              Read All Reviews <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* REAL MEN GALLERY */}
      <section
        className="relative border-t border-white/10 py-24 md:py-32"
        data-testid="lifestyle-gallery-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
              <div>
                <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                  04 / THE LIFESTYLE
                </div>
                <h2
                  className="font-display uppercase leading-[0.9] tracking-tight"
                  style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
                >
                  Sharp men. <br className="md:hidden" />
                  <span className="text-stroke">Sharper standards.</span>
                </h2>
              </div>
              <p className="hidden md:block max-w-sm text-white/60 leading-relaxed">
                Primenax isn't a supplement brand. It's a standard. Watches.
                Suits. Conversations you control. We make the protocol for the
                man who's already there.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {LIFESTYLE_GALLERY.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <article className="group relative overflow-hidden border border-white/10 hover:border-[#D10000]/50 transition-colors aspect-[4/5]">
                  <img
                    src={g.src}
                    alt={g.caption}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-2">
                      {String(i + 1).padStart(2, "0")} · {g.label}
                    </div>
                    <p className="font-display text-xl md:text-2xl tracking-tight uppercase leading-tight">
                      {g.caption}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="mt-12 md:mt-16 flex justify-center">
              <Link
                to="/shop"
                data-testid="cta-lifestyle-shop-link"
                className="inline-flex items-center gap-3 bg-[#D10000] hover:bg-[#A30000] text-white font-display text-lg md:text-xl tracking-widest uppercase px-8 py-4 transition-colors"
              >
                Shop the Stack
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE MAGNETIC EFFECT — sex appeal */}
      <section
        className="relative border-t border-white/10 py-24 md:py-32 grain"
        data-testid="attraction-gallery-section"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 80% 30%, rgba(209,0,0,0.35), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 md:mb-16">
              <div className="lg:col-span-7">
                <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                  05 / THE EFFECT
                </div>
                <h2
                  className="font-display uppercase leading-[0.9] tracking-tight"
                  style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
                >
                  Walk in <br /> <span className="text-stroke">the room.</span>
                </h2>
              </div>
              <div className="lg:col-span-5 flex items-end">
                <p className="text-white/70 leading-relaxed max-w-md">
                  Drive doesn't just live in the bedroom — it lives in how you
                  show up. Primenax is built for the man who's already
                  magnetic and wants to amplify it.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ATTRACTION_GALLERY.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <article className="group relative overflow-hidden border border-white/10 hover:border-[#D10000]/50 transition-colors aspect-[3/4]">
                  <img
                    src={g.src}
                    alt={g.caption}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-2">
                      {String(i + 1).padStart(2, "0")} · {g.label}
                    </div>
                    <p className="font-display text-xl md:text-2xl tracking-tight uppercase leading-tight">
                      {g.caption}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <p className="mt-10 text-white/40 text-xs max-w-xl">
              * Images depict lifestyle. Individual results vary. Primenax is a
              dietary supplement; it is not a substitute for medical advice,
              and is not intended to diagnose, treat, cure, or prevent any
              disease.
            </p>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="mt-10 md:mt-12 flex justify-center">
              <Link
                to="/shop"
                data-testid="cta-effect-shop-link"
                className="inline-flex items-center gap-3 bg-[#D10000] hover:bg-[#A30000] text-white font-display text-lg md:text-xl tracking-widest uppercase px-8 py-4 transition-colors"
              >
                Shop the Stack
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
